import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import {
  HeartPulse, Search, UserPlus, Send, CheckCircle2, ChevronRight, X, Mail, Users, AlertTriangle, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreditBar = ({ icon: Icon, label, used, limit }) => {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const remaining = Math.max(0, (limit || 0) - (used || 0));
  const low = limit > 0 && remaining / limit <= 0.1;
  return (
    <div data-testid={`credit-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="flex items-center gap-1.5 text-slate-600"><Icon className="w-4 h-4 text-[#00aeef]" /> {label}</span>
        <span className={`font-semibold ${low ? 'text-amber-600' : 'text-slate-800'}`}>{remaining.toLocaleString()} left</span>
      </div>
      <Progress value={pct} className="h-2" />
      <p className="text-[11px] text-slate-400 mt-1">{(used || 0).toLocaleString()} of {(limit || 0).toLocaleString()} used this month</p>
    </div>
  );
};

const Step = ({ done, num, icon: Icon, title, desc, cta, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`text-left w-full rounded-xl border p-4 transition-colors ${done ? 'border-emerald-200 bg-emerald-50/50' : 'border-gray-200 hover:border-[#00aeef] hover:bg-sky-50/40'}`}
  >
    <div className="flex items-start gap-3">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${done ? 'bg-emerald-500 text-white' : 'bg-[#00aeef]/10 text-[#00aeef]'}`}>
        {done ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Step {num}</span>
          {done && <span className="text-[10px] font-semibold text-emerald-600">Done</span>}
        </div>
        <p className="text-sm font-semibold text-slate-800 mt-0.5">{title}</p>
        <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
        {!done && <span className="inline-flex items-center gap-1 text-xs font-medium text-[#00aeef] mt-2">{cta} <ChevronRight className="w-3 h-3" /></span>}
      </div>
    </div>
  </button>
);

/** First-login welcome card for supplier sub-accounts: credits + 3-step quick-start. */
export const SupplierOnboarding = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(localStorage.getItem('supplier_quickstart_dismissed') === '1');
  const [requested, setRequested] = useState(false);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/supplier/summary`).then(({ data }) => {
      setData(data);
      setRequested(!!data.credit_request_pending);
    }).catch(() => {});
  }, []);

  if (!data || !data.is_supplier) return null;

  const c = data.credits;
  const lowResource = (r) => r && r.limit > 0 && (r.limit - r.used) / r.limit <= 0.10;
  const isLow = c && (lowResource(c.emails) || lowResource(c.contacts));

  const requestCredits = async () => {
    setRequesting(true);
    try {
      const { data: res } = await axios.post(`${API}/supplier/request-credits`, {});
      setRequested(true);
      toast.success(res.already_requested ? 'Your agency has already been notified' : 'Your agency has been notified - they\'ll top up your credits shortly');
    } catch (e) {
      toast.error('Could not send request');
    } finally {
      setRequesting(false);
    }
  };

  const p = data.progress || {};
  const step1 = (p.companies || 0) > 0;
  const step2 = (p.contacts || 0) > 0;
  const step3 = (p.campaigns_sent || 0) > 0;
  const allDone = step1 && step2 && step3;

  const dismiss = () => { localStorage.setItem('supplier_quickstart_dismissed', '1'); setDismissed(true); };

  return (
    <Card className="border-[#00aeef]/30 bg-gradient-to-br from-sky-50/70 to-white overflow-hidden" data-testid="supplier-onboarding">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#00aeef]/10 flex items-center justify-center">
              <HeartPulse className="w-6 h-6 text-[#00aeef]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Welcome{data.org_name ? ` to ${data.org_name}` : ''} 👋</h2>
              <p className="text-sm text-slate-500">Your self-service workspace - here's your monthly allowance and a quick start.</p>
            </div>
          </div>
          {allDone && !dismissed && (
            <button onClick={dismiss} className="text-slate-400 hover:text-slate-600" title="Dismiss" data-testid="dismiss-quickstart"><X className="w-4 h-4" /></button>
          )}
        </div>

        {/* Credits */}
        {data.credits && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
            <CreditBar icon={Mail} label="Emails" used={data.credits.emails.used} limit={data.credits.emails.limit} />
            <CreditBar icon={Users} label="Contacts" used={data.credits.contacts.used} limit={data.credits.contacts.limit} />
          </div>
        )}

        {/* Low-balance nudge */}
        {isLow && (
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="low-balance-nudge">
            <div className="flex items-center gap-2 flex-1 text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              You're running low on this month's credits. Need more to keep reaching out?
            </div>
            <Button
              size="sm"
              onClick={requestCredits}
              disabled={requesting || requested}
              className="bg-amber-500 hover:bg-amber-600 text-white shrink-0"
              data-testid="request-credits-btn"
            >
              {requesting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              {requested ? 'Request sent' : 'Request more credits'}
            </Button>
          </div>
        )}

        {/* Quick-start (hidden once all done + dismissed) */}
        {!(allDone && dismissed) && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
            <Step done={step1} num={1} icon={Search} testid="quickstart-step-1"
              title="Find care providers" desc="Browse the CQC care directory and add providers to your CRM."
              cta="Open Care Directory" onClick={() => navigate('/admin/care-directory')} />
            <Step done={step2} num={2} icon={UserPlus} testid="quickstart-step-2"
              title="Build your contacts" desc="Add the people you want to reach at each provider."
              cta="Go to Contacts" onClick={() => navigate('/admin/contacts')} />
            <Step done={step3} num={3} icon={Send} testid="quickstart-step-3"
              title="Send your first campaign" desc="Launch a care-tuned email campaign to your list."
              cta="Create a campaign" onClick={() => navigate('/admin/campaigns')} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
